import { useContext, useState } from 'react'
import { useRouter } from 'next/router'
import { gql } from '@apollo/client'
import { Box, Heading, Link } from '@chakra-ui/react'
import { PtInput, PtBtn, PtAck, Ptext } from '../../componentsF/Chakra3F/ChakraF.jsx'
import { useMutationNext, useQueryNext } from '../../componentsF/hooksF/useQueryNextF.js'
import { useOneToast, useToastOnError } from '../../componentsF/hooksF/useToastOnErrF.mts'
import { UserAuthContext, doRmCookie, useReloadOnLogin } from '../../componentsF/UserAuthWrapperF.jsx'
import { PubEnvCtx } from '../../componentsF/PubEnvWrapF.jsx'
import { CenteredSpinner } from '../../componentsF/CustomSiteF/CenteredSpinnerF.jsx'
import { GamePgCont } from './common/GenericGamePage.jsx'
import { useLocalStorage } from '../../componentsF/localStorageF/LocalStorWrapF.tsx'
import { RoundedButton } from './GameCompsF.jsx'

const reqGamesEmailLoginMut = gql`mutation ($email: String!, $returnpath: String) {
  gmReqGamesEmailLogin(email: $email, returnpath: $returnpath)
}`

export const createGameRoomMut = gql`mutation {
  gmCreateGameRoom
}`

const selfHostUrl = 'https://peat.eth.demokluster.com#drawinggame'

const ReadOnlyLandingSection = () => {
  return <PtAck {...{spacing: 3, w: 'sm', textAlign: 'center'}}>
    <Ptext {...{fontSize: 'md'}}>
      This drawinggame instance is read-only to prevent the hn hug-of-death.
    </Ptext>
    <Link {...{href: selfHostUrl, isExternal: true, color: 'purple.400'}}>
      You can totally self-host your own tho!
    </Link>
  </PtAck>
}

const JoinGameSection = () => {
  return <PtAck {...{spacing: 3, w: 'sm', textAlign: 'center'}}>
    <Heading {...{size: '2xl', mb: 2}}>Join a Private Game</Heading>
    <Ptext {...{fontSize: 'md', color: 'gray.400'}}>
      To join a private game, please get the URL or QR code from the host
    </Ptext>
  </PtAck>
}

export const LoginSection = () => {
  const [email, setEmail] = useLocalStorage('gpEmail')
  const {pathname} = useRouter()
  const [doSend, {loading, error, data}] = useMutationNext(reqGamesEmailLoginMut)

  useReloadOnLogin()
  useToastOnError({error})
  useOneToast(data && {
    description: 'Check your email for a login link.',
    status: 'info',
  })

  const onClick = () => {
    if (!email) return
    doSend({variables: {email, returnpath: pathname}})
  }
  const onKeyDown = (evt) => {
    if (evt.key === 'Enter') onClick()
  }
  return <PtAck {...{spacing: 3, w: 'sm'}}>
    <Heading {...{size: 'lg'}}>Login</Heading>
    <PtInput {...{
      type: 'email',
      placeholder: 'email@address.com',
      textAlign: 'center',
      value: email || '',
      onChange: (e) => setEmail(e.target.value),
      onKeyDown,
    }} />
    <RoundedButton {...{onClick, isLoading: loading, bg: 'purple.400', color: 'white', fontSize: '2xl'}}>
      Send Login Link
    </RoundedButton>
  </PtAck>
}

export const useCreateGameRoom = () => {
  const router = useRouter()
  const [doCreate, {loading, error}] = useMutationNext(createGameRoomMut)
  useToastOnError({error})
  const onCreateGame = async () => {
    const {data} = await doCreate()
    const gameRoom = data?.gmCreateGameRoom
    if (gameRoom) router.push(`/${gameRoom}`)
  }
  return {onCreateGame, createLoading: loading}
}

export const useLogout = () => {
  const onLogout = () => { doRmCookie(); location.reload() }
  return {onLogout}
}

// Base LoggedInSection - no billing UI (used by gamesapp/pt0)
// Does NOT query gmHostedGames since that resolver only exists in gamespay
const LoggedInSection = () => {
  const {onCreateGame, createLoading} = useCreateGameRoom()
  const {onLogout} = useLogout()

  return <PtAck {...{spacing: 4, w: 'sm'}}>
    <RoundedButton {...{onClick: onCreateGame, isLoading: createLoading, fontSize: '2xl', bg: 'purple.400', color: 'white'}}>
      Create a Game
    </RoundedButton>
    <RoundedButton {...{onClick: onLogout, fontSize: 'xl', bg: 'gray.600', color: 'white'}}>
      Logout
    </RoundedButton>
  </PtAck>
}

export const GamesLandingPageBase = (/** @type {{LoggedInSectionOverride?: import('react').ComponentType, JoinSectionOverride?: import('react').ComponentType, LoginSectionOverride?: import('react').ComponentType}} */ {LoggedInSectionOverride, JoinSectionOverride, LoginSectionOverride}) => {
  const authCtx = useContext(UserAuthContext)
  const {isLoggedIn, isLoading} = authCtx || {}
  const {gpIsReadOnly} = /** @type {any} */ (useContext(PubEnvCtx) || {})
  const LoggedInComp = LoggedInSectionOverride || LoggedInSection
  const JoinComp = JoinSectionOverride || JoinGameSection
  const LoginComp = LoginSectionOverride || LoginSection

  return <GamePgCont>
    <PtAck {...{spacing: 8, alignItems: 'center'}}>
      {gpIsReadOnly ? <ReadOnlyLandingSection /> : <>
        <JoinComp />
        <Box {...{borderTop: '1px solid', borderColor: 'gray.600', w: 'sm', my: 2}} />
        {isLoading
          ? <CenteredSpinner />
          : isLoggedIn
            ? <LoggedInComp />
            : <LoginComp />
        }
      </>}
    </PtAck>
  </GamePgCont>
}

// Default export for gamesapp (pt0) - no billing
export const GamesLandingPage = () => <GamesLandingPageBase />
